import { ApiService } from '../ApiService';
import type { Message, Conversation, AIModel } from '../../../types/chat';
import type { ApiResponse } from '../types';
import type { JournalEntry } from '../../../types/accounting';

/**
 * Requête d'envoi de message - conforme à la documentation API
 * Si conversationId est absent, une nouvelle conversation est automatiquement créée
 */
export interface ChatMessageRequest {
  conversationId?: string;        // Optionnel - si absent, nouvelle conversation créée
  message: {
    content: string;
    attachment?: {
      name: string;
      type: string;
      content: string;             // base64
    };
  };
  modelId?: string;                // ID du modèle IA (défaut: adha-1)
  writeMode?: boolean;             // Mode écriture comptable
  context?: string[];              // Contexte additionnel (ex: fiscal-year-2024)
}

/**
 * Réponse d'envoi de message - conforme à la documentation API
 */
export interface ChatMessageResponse {
  message: Message;
  conversationId: string;          // ID de la conversation (nouvelle ou existante)
  journalEntry?: JournalEntry;     // Écriture proposée si writeMode=true
}

/**
 * Réponse de l'endpoint streaming - conforme à la documentation API v2.3.0
 */
export interface ChatStreamResponse {
  messageId: string;
  conversationId: string;
  userMessageId: string;
}

/**
 * Liste des conversations avec pagination
 */
export interface ChatConversationsResponse {
  conversations: Conversation[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export const chatApi = {
  /**
   * Envoie un message et reçoit une réponse de l'assistant
   * Si conversationId n'est pas fourni, une nouvelle conversation est créée
   * @see POST /chat
   */
  sendMessage: (request: ChatMessageRequest): Promise<ApiResponse<ChatMessageResponse>> => 
    ApiService.post<ChatMessageResponse>('/chat', request),

  /**
   * Envoie un message à une conversation existante (endpoint alternatif)
   * @see POST /chat/message
   */
  sendMessageToConversation: (request: ChatMessageRequest): Promise<ApiResponse<ChatMessageResponse>> => 
    ApiService.post<ChatMessageResponse>('/chat/message', request),

  /**
   * Envoie un message avec streaming - Retour immédiat
   * Les réponses arrivent via WebSocket sur le namespace /chat
   * @see POST /chat/stream
   * @since API v2.3.0
   */
  sendStreamMessage: (request: ChatMessageRequest): Promise<ApiResponse<ChatStreamResponse>> => 
    ApiService.post<ChatStreamResponse>('/chat/stream', request),

  /**
   * Récupère la liste des conversations de l'utilisateur
   * @see GET /chat/conversations
   */
  getConversations: (params?: { page?: number; pageSize?: number }): Promise<ApiResponse<ChatConversationsResponse>> => 
    ApiService.get<ChatConversationsResponse>('/chat/conversations', params),

  /**
   * Récupère le détail d'une conversation avec tous ses messages
   * @see GET /chat/conversations/{id}
   */
  getConversationById: (id: string): Promise<ApiResponse<Conversation>> => 
    ApiService.get<Conversation>(`/chat/conversations/${id}`),

  /**
   * Supprime une conversation et tous ses messages
   * @see DELETE /chat/conversations/{id}
   */
  deleteConversation: (id: string): Promise<ApiResponse<void>> => 
    ApiService.delete<void>(`/chat/conversations/${id}`),
  
  /**
   * Récupère la liste des modèles IA disponibles
   * @see GET /chat/models
   */
  getAvailableModels: (): Promise<ApiResponse<{ models: AIModel[] }>> => 
    ApiService.get<{ models: AIModel[] }>('/chat/models')
};
